import { useState, useCallback, useRef, useEffect } from 'react';

/**
 * useFormDirty — tracks whether a form has been modified.
 *
 * Usage:
 *   const { dirty, markDirty, resetDirty, confirmIfDirty } = useFormDirty();
 *
 *   // In your form field handler:
 *   function handleField(name, value) {
 *     setForm(prev => ({ ...prev, [name]: value }));
 *     markDirty();
 *   }
 *
 *   // Pass dirty to Modal:
 *   <Modal dirty={dirty} ...>
 *
 *   // After successful save:
 *   resetDirty();
 *
 *   // Before leaving a detail view:
 *   function goBack() {
 *     if (confirmIfDirty()) { setDetailView(null); }
 *   }
 */
export function useFormDirty() {
  const [dirty, setDirty] = useState(false);

  const markDirty = useCallback(() => {
    setDirty(true);
  }, []);

  const resetDirty = useCallback(() => {
    setDirty(false);
  }, []);

  /**
   * Returns true if it's safe to leave (not dirty, or user confirmed).
   * Returns false if user chose to stay.
   */
  const confirmIfDirty = useCallback(() => {
    if (!dirty) return true;
    return window.confirm('You have unsaved changes. Do you want to discard them?');
  }, [dirty]);

  return { dirty, markDirty, resetDirty, confirmIfDirty };
}
